import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { REQUEST_TYPES } from "../constants";
import { createRequest } from "../services/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, Gem, Send, Save } from "lucide-react";
import { toast } from "sonner";

export default function RequestForm() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ requestTitle: "", requestType: "", gemsAmount: "", description: "", optionalNotes: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const validate = () => {
    const e = {};
    if (!f.requestTitle.trim()) e.requestTitle = "Title is required.";
    if (!f.requestType) e.requestType = "Select a request type.";
    if (!f.description.trim()) e.description = "Description is required.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handle = async (asDraft) => {
    if (!asDraft && !validate()) return;
    if (asDraft && !f.requestTitle.trim()) { setErrors({ requestTitle: "Add a title to save a draft." }); return; }
    setBusy(true);
    try {
      await createRequest({
        requestTitle: f.requestTitle, requestType: f.requestType || "Other",
        gemsAmount: Number(f.gemsAmount) || 0, description: f.description,
        optionalNotes: f.optionalNotes, asDraft,
      });
      toast.success(asDraft ? "Draft saved." : "Request submitted to HR and Director.");
      navigate("/app/requests");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit request.");
    } finally {
      setBusy(false);
    }
  };

  const fieldCls = "bg-vault-panel2 border-white/10 text-vault-cream placeholder:text-vault-subtle/60";
  const ro = "rounded-xl bg-vault-panel2/60 border border-white/5 px-3 py-2.5 text-sm text-vault-cream";

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm font-medium text-vault-panel/70 hover:text-vault-panel transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="rounded-3xl bg-vault-panel p-7 sm:p-9">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-vault-accent mb-2">New Submission</div>
        <h1 className="font-display text-3xl font-extrabold text-vault-cream">Request Form</h1>
        <p className="text-sm text-vault-subtle mt-2">Submit a request. It will route to HR for review, then Director for final approval.</p>

        <div className="grid sm:grid-cols-2 gap-3 mt-7">
          <div><div className="text-xs text-vault-subtle mb-1">Employee Name</div><div className={ro}>{user.firstName} {user.lastName}</div></div>
          <div><div className="text-xs text-vault-subtle mb-1">Email</div><div className={ro}>{user.email}</div></div>
          <div><div className="text-xs text-vault-subtle mb-1">Location</div><div className={ro}>{user.primaryLocation}</div></div>
          <div><div className="text-xs text-vault-subtle mb-1">Role</div><div className={ro}>{user.role}</div></div>
        </div>

        <div className="space-y-5 mt-6">
          <div className="space-y-2">
            <Label className="text-vault-subtle">Request Title</Label>
            <Input value={f.requestTitle} onChange={(e) => set("requestTitle", e.target.value)} className={`${fieldCls} h-11`} placeholder="e.g. Redeem gems for a half-day off" />
            {errors.requestTitle && <p className="text-xs text-red-400">{errors.requestTitle}</p>}
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label className="text-vault-subtle">Request Type</Label>
              <Select value={f.requestType} onValueChange={(v) => set("requestType", v)}>
                <SelectTrigger className={`${fieldCls} h-11`}><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent>{REQUEST_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              {errors.requestType && <p className="text-xs text-red-400">{errors.requestType}</p>}
            </div>
            <div className="space-y-2">
              <Label className="text-vault-subtle">Gems Amount / Cost</Label>
              <div className="relative">
                <Gem className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-vault-accent" />
                <Input type="number" min="0" value={f.gemsAmount} onChange={(e) => set("gemsAmount", e.target.value)} className={`${fieldCls} h-11 pl-9`} placeholder="0" />
              </div>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-vault-subtle">Description / Reason for Request</Label>
            <Textarea value={f.description} onChange={(e) => set("description", e.target.value)} className={`${fieldCls} min-h-[110px]`} placeholder="Describe your request..." />
            {errors.description && <p className="text-xs text-red-400">{errors.description}</p>}
          </div>
          <div className="space-y-2">
            <Label className="text-vault-subtle">Optional Notes</Label>
            <Textarea value={f.optionalNotes} onChange={(e) => set("optionalNotes", e.target.value)} className={`${fieldCls} min-h-[70px]`} placeholder="Anything else to add?" />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 mt-8">
          <button disabled={busy} onClick={() => handle(false)} className="inline-flex items-center gap-2 rounded-2xl bg-vault-accent text-vault-panel font-bold px-6 py-3 hover:bg-vault-accentdark transition-colors disabled:opacity-60">
            <Send className="h-4 w-4" /> Submit Request
          </button>
          <button disabled={busy} onClick={() => handle(true)} className="inline-flex items-center gap-2 rounded-2xl bg-vault-panel3 text-vault-cream font-semibold px-6 py-3 hover:bg-vault-panel2 transition-colors disabled:opacity-60">
            <Save className="h-4 w-4" /> Save as Draft
          </button>
        </div>
      </div>
    </div>
  );
}
